/**
 * Client-side helper to trigger the welcome email for a user.
 * Call after Firestore users/{uid} has been created/updated with email.
 * Idempotent: server no-ops if emailSentAt.welcome already set.
 * Await this before navigating away so the request is not cancelled.
 */

const WELCOME_API_TIMEOUT_MS = 10_000;

export async function triggerWelcomeEmail(uid: string): Promise<void> {
  if (!uid?.trim()) {
    console.warn('[welcome-trigger] skip: no uid');
    return;
  }
  console.log('[welcome-trigger] calling /api/email/welcome', { uid });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WELCOME_API_TIMEOUT_MS);
  try {
    const res = await fetch('/api/email/welcome', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: uid.trim() }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const raw = await res.text();
    const data = isJson ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : {};
    if (!isJson) {
      console.warn('[welcome-trigger] response was not JSON (API may be missing in production)', { uid, status: res.status, contentType, snippet: raw.slice(0, 60) });
    }
    if (res.ok && isJson) {
      if (data.skipped) {
        console.log('[welcome-trigger] ok (already_sent)', { uid });
      } else {
        console.log('[welcome-trigger] ok (sent)', { uid });
      }
    } else if (isJson) {
      console.warn('[welcome-trigger] non-ok response', { uid, status: res.status, error: data?.error });
    }
  } catch (err) {
    clearTimeout(timeoutId);
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('[welcome-trigger] failed', { uid, error: msg });
  }
}
